import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, Tooltip, Tabs, Tab, Badge } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import HeaderRec from '../../header/headerRec';
import Footer from '../../commun/Footer';
import './TopMatches.css';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from '../../commun/Pagination';
import SendIcon from '@mui/icons-material/Send';
import { Button, Chip } from '@mui/material';

const TopMatches = () => {
  const [talentsWithMissions, setTalentsWithMissions] = useState({
    adminTalents: [],
    recruiterTalents: [],
  });
  const recruteurName = localStorage.getItem('recruteurName');
  const authToken = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [niveau, setNiveau] = useState(5);
  const [listTab, setListTab] = useState('recruteur');  
  const itemsPerPage = 10;

  useEffect(() => setPage(1), [listTab]);

  useEffect(() => {
    const fetchTalentMatchScores = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/recruteur/talent/top-match?recruteurId=${userId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const data = await response.json();
        setTalentsWithMissions({
          adminTalents: Array.isArray(data.adminTalents) ? data.adminTalents : [],
          recruiterTalents: Array.isArray(data.recruiterTalents) ? data.recruiterTalents : [],
        });
        setNiveau(data?.niveau?.niveau ?? 5);
      } catch (err) {
        console.error('Erreur récupération similarités:', err);
        setTalentsWithMissions({ adminTalents: [], recruiterTalents: [] });
      }
    };
    fetchTalentMatchScores();
  }, [API_BASE, authToken, userId, niveau]);

  const getAdminTalentLimit = (niv) => {
    switch (niv) {
      case 1: return 5;
      case 2: return 10;
      case 3: return 20;
      case 4: return 30;
      case 5: return 40;
      default: return 10;
    }
  };

  const limit = getAdminTalentLimit(niveau);

  const limitedAdminTalents = useMemo(
    () => (talentsWithMissions.adminTalents || []).slice(0, limit),
    [talentsWithMissions.adminTalents, limit]
  );

  const isKept = (t) => (t?.missionMatches?.[0]?.similarity ?? 0) * 100 > 10;

  const adminFiltered = useMemo(
    () => limitedAdminTalents.filter(isKept),
    [limitedAdminTalents]
  );
  const recruiterFiltered = useMemo(
    () => (talentsWithMissions.recruiterTalents || []).filter(isKept),
    [talentsWithMissions.recruiterTalents]
  );
  const allFiltered = useMemo(
    () => [...adminFiltered, ...recruiterFiltered],
    [adminFiltered, recruiterFiltered]
  );

  const sourceList = useMemo(() => {
    if (listTab === 'admin') return adminFiltered;
    if (listTab === 'recruteur') return recruiterFiltered;
    return allFiltered;
  }, [listTab, adminFiltered, recruiterFiltered, allFiltered]);

  const paginatedTalents = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sourceList.slice(start, start + itemsPerPage);
  }, [sourceList, page, itemsPerPage]);

  const adminIdSet = useMemo(() => new Set(limitedAdminTalents.map(t => t._id)), [limitedAdminTalents]);
  const recruiterIdSet = useMemo(() => new Set((talentsWithMissions.recruiterTalents || []).map(t => t._id)), [talentsWithMissions.recruiterTalents]);

  const adminTalentsOnPage = useMemo(
    () => paginatedTalents.filter(t => adminIdSet.has(t._id)),
    [paginatedTalents, adminIdSet]
  );
  const recruiterTalentsOnPage = useMemo(
    () => paginatedTalents.filter(t => recruiterIdSet.has(t._id)),
    [paginatedTalents, recruiterIdSet]
  );

  const renderTalentCard = (talent) => {
    const mission = talent.missionMatches[0].mission;
    const similarity = (talent.missionMatches[0].similarity * 100).toFixed(0);
    const isAdminTalent = adminIdSet.has(talent._id);

    return (
      <Paper
        className={`mission-card styled-card ${isAdminTalent ? 'admin-talent' : ''}`}
        key={`${mission._id}-${talent._id}`}
      >
        <Box className="card-content">
          <Box className="company-icon">
            <BusinessIcon fontSize="large" color="action" />
            <Typography className="mission-subtitle">
              <Link
                to={`/profil-entreprise/${mission.entreprise?.utilisateur}`}
                className="talent-name-link"
              >
                {mission.entreprise?.NomEntreprise || 'Entreprise inconnue'}
              </Link>
            </Typography>
          </Box>

          <Box className="mission-info">
            <Typography className="mission-title">
              {mission.nomMission || 'Titre inconnu'}
            </Typography>
            <Typography className="mission-subtitle">
              {mission.lieuxTravail || 'Lieu inconnu'}
            </Typography>
            <Typography className="mission-subtitle">
              Places disponible : {mission.placesDispo}
            </Typography>
            <button
              className="btn-detail"
              onClick={() => navigate(`/Recruteur/Details/${mission._id}`)}
            >
              Voir la mission
            </button>
          </Box>

          <Box className="compatibility">
            <Typography className="compat-score">{similarity}%</Typography>
            <Typography className="compat-label">COMPATIBILITÉ</Typography>
          </Box>

          <Box className="talent-info-box">
            <Box className="avatar-circle">
              {talent.prenomCandidat?.charAt(0)}
              {talent.nomCandidat?.charAt(0)}
            </Box>
            <Box>
              <Link
                to={`/Recruteur/talent-detail/${talent._id}`}
                className="talent-name-link"
              >
                <Typography className="talent-name">
                  {talent.prenomCandidat} {talent.nomCandidat}
                </Typography>
              </Link>
              <Typography className="talent-role">
                {talent.pointsEssentiels &&
                talent.pointsEssentiels.length > 0 &&
                talent.pointsEssentiels[0].replace(/"/g, '').trim() !== ''
                  ? talent.pointsEssentiels[0].replace(/"/g, '')
                  : 'Non précisé'}
              </Typography>

              <button
                className="btn-detail"
                onClick={() => navigate(`/Recruteur/talent-detail/${talent._id}`)}
              >
                Détail
              </button>
            </Box>
          </Box>

          <Tooltip title="Positionner ce talent sur la mission" arrow>
            <button
              className="partager-btn"
              onClick={() =>
                navigate(`/recruteur/travailler-mission/${mission._id}?t=${talent._id}`)
              }
            >
              <SendIcon className="partager-icon" />
              Positionner
            </button>
          </Tooltip>
        </Box>
      </Paper>
    );
  };

  return (
    <>
      <HeaderRec recruteurName={recruteurName} authToken={authToken} userId={userId} />
      <main className="pageContainer">
        <h2 className="pageTitle">Mes top matches</h2>

        {/* Tabs + counts */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {/* Custom tab buttons */}
          <Box className="statusTabsWrapper">
            {[
              { key: 'recruteur', label: 'Recruteur', color: '#1976d2', count: recruiterFiltered.length },
              { key: 'admin', label: 'Admin', color: '#2e7d32', count: adminFiltered.length },
              { key: 'tous', label: 'Tous', color: '#9e9e9e', count: allFiltered.length },
            ].map(({ key, label, color, count }) => {
              const isActive = listTab === key;
              return (
                <Button
                  key={key}
                  className={`statusBtn ${isActive ? 'active' : ''}`}
                  onClick={() => setListTab(key)}
                  variant={isActive ? 'contained' : 'outlined'}
                  style={{
                    borderColor: color,
                    backgroundColor: isActive ? color : 'transparent',
                    color: isActive ? '#fff' : '#333',
                  }}
                >
                  {label}
                  <Chip
                    label={count}
                    size="small"
                    className="statusChip"
                    style={{
                      backgroundColor: isActive ? '#fff' : color,
                      color: isActive ? color : '#fff',
                    }}
                  />
                </Button>
              );
            })}
          </Box>

          <span className="total-count">
            Total des talents : <b>{sourceList.length}</b>
          </span>
        </Box>

        <Box className="top-matches-container">
          {sourceList.length === 0 ? (
            <Typography variant="body1" className="no-results-message">
              Aucun talent avec une mission ayant un score de compatibilité supérieur à 60%.
            </Typography>
          ) : (
            <>
              {listTab === 'tous' ? (
                <>
                  {adminTalentsOnPage.length > 0 && (
                    <>
                      <Paper className="section-header-card admin-header" elevation={6}>
                        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                          🎖️ Votre récompense pour votre performance
                        </Typography>
                      </Paper>
                      {adminTalentsOnPage.map(renderTalentCard)}
                    </>
                  )}

                  {recruiterTalentsOnPage.length > 0 && (
                    <>
                      <Paper className="section-header-card recruiter-header" elevation={6}>
                        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                          💼 Découvrez vos meilleurs talents
                        </Typography>
                      </Paper>
                      {recruiterTalentsOnPage.map(renderTalentCard)}
                    </>
                  )}
                </>
              ) : (
                <>
                  {listTab === 'admin' && (
                    <>
                      <Paper className="section-header-card admin-header" elevation={6}>
                        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                          🎖️ Votre récompense pour votre performance
                        </Typography>
                      </Paper>
                      {paginatedTalents.map(renderTalentCard)}
                    </>
                  )}

                  {listTab === 'recruteur' && (
                    <>
                      <Paper className="section-header-card recruiter-header" elevation={6}>
                        <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                          💼 Découvrez vos meilleurs talents
                        </Typography>
                      </Paper>
                      {paginatedTalents.map(renderTalentCard)}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        <Pagination
          page={page}
          setPage={setPage}
          totalItems={sourceList.length}
          itemsPerPage={itemsPerPage}
        />

        <Footer />
      </main>
    </>
  );
};

export default TopMatches;
